const STALE_CHUNK_RELOAD_KEY = 'wren:stale-chunk-reload';
const STALE_CHUNK_RELOAD_COOLDOWN_MS = 30_000;

function latestReloadMarker(): string | null {
  const markers: string[] = [];
  try {
    const stored = window.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY);
    if (stored) markers.push(stored);
  } catch {
    // Storage can be disabled in privacy-focused browser modes.
  }
  const historyMarker = window.history.state?.[STALE_CHUNK_RELOAD_KEY];
  if (typeof historyMarker === 'string') markers.push(historyMarker);
  if (markers.length === 0) return null;
  const validMarkers = markers.filter((marker) =>
    Number.isFinite(Number(marker))
  );
  return validMarkers.length > 0
    ? validMarkers.sort((left, right) => Number(right) - Number(left))[0]
    : markers[0];
}

function persistReloadMarker(now: number): boolean {
  const marker = String(now);
  let persisted = false;
  try {
    window.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, marker);
    persisted = true;
  } catch {
    // Fall through to history state, which survives a same-page reload.
  }
  try {
    const currentState =
      window.history.state && typeof window.history.state === 'object'
        ? window.history.state
        : {};
    window.history.replaceState(
      { ...currentState, [STALE_CHUNK_RELOAD_KEY]: marker },
      '',
      window.location.href
    );
    persisted = true;
  } catch {
    // If neither persistence mechanism works, do not risk a reload loop.
  }
  return persisted;
}

/**
 * Builds an error that {@link isChunkLoadError} recognizes, so the stale-chunk
 * recovery path can treat it like a native lazy-import failure. Used when a
 * dynamic import resolves to a module that is missing its expected export —
 * which surfaces as a generic `Cannot read properties of undefined` TypeError
 * rather than a module-load error, and would otherwise slip past recovery.
 */
export function createChunkLoadError(chunkName: string): Error {
  const error = new Error(`Failed to load chunk: ${chunkName}`);
  error.name = 'ChunkLoadError';
  return error;
}

export function isChunkLoadError(error: unknown): boolean {
  const { name = '', message = '' } = (error ?? {}) as {
    name?: string;
    message?: string;
  };
  const normalizedMessage = message.toLowerCase();

  return (
    name === 'ChunkLoadError' ||
    normalizedMessage.includes('dynamically imported module') ||
    normalizedMessage.includes('importing a module script failed') ||
    normalizedMessage.includes('error loading a module script')
  );
}

export function shouldReloadForStaleChunk(
  lastReload: string | null,
  now = Date.now()
): boolean {
  if (!lastReload) return true;

  const lastReloadAt = Number(lastReload);
  return (
    !Number.isFinite(lastReloadAt) ||
    now - lastReloadAt >= STALE_CHUNK_RELOAD_COOLDOWN_MS
  );
}

export function reloadForStaleChunk(
  reload: () => void = () => window.location.reload()
): boolean {
  const lastReload = latestReloadMarker();
  if (!shouldReloadForStaleChunk(lastReload)) return false;
  if (!persistReloadMarker(Date.now())) return false;
  reload();
  return true;
}

export function installStaleChunkRecovery() {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForStaleChunk()) {
      // Suppress the lazy import error while the browser loads the new build.
      event.preventDefault();
    }
  });
}
